import React, { useState, useEffect, useRef } from 'react'
import {
  Award, CheckCircle, XCircle, Clock, RotateCcw,
  ChevronRight, AlertTriangle, Star, Target, Brain,
  PenTool, BookOpen, Lightbulb,
  ChevronDown, ChevronUp, Trophy, Zap, BarChart2, Youtube,
  Minus, Plus, X
} from 'lucide-react'
import { useProgressStore, PASS_SCORE, useUnitProgress } from '../store/progressStore'
import ConceptVideoModal from '../components/ConceptVideoModal'
import { useVideoStore } from '../store/videoStore'
import {
  computePredictedFragments, FRAGMENT_TAG_CONFIG,
  type PredictedFragment
} from '../store/predictedFragments'

const UNIT_ID = 'A1-2'

interface TestQuestion {
  id: string
  number: number
  category: string
  difficulty: 'easy' | 'medium' | 'hard'
  question: string
  choices: string[]
  answer: number
  explanation: string[]
  concept: string
  misconception?: string
  misconceptionCode?: string
  learnStepId?: string
}

const TEST_QUESTIONS: TestQuestion[] = [
  {
    id: 'Q01', number: 1, category: '방정식과 해', difficulty: 'easy',
    question: 'x=2일 때, 방정식 3x-1=5의 해인지 확인하면?',
    choices: ['해이다 (3×2-1=5 ✓)', '해가 아니다 (3×2-1≠5)', '해이다 (2+3=5 ✓)', '알 수 없다'],
    answer: 0,
    explanation: [
      'x=2 대입: 3×2-1 = 6-1 = 5',
      '우변: 5',
      '좌변=우변이므로 x=2는 해 ✓',
    ],
    concept: '방정식과 해', misconceptionCode: 'BE00', learnStepId: 'AB01',
    misconception: '해 확인: 좌변과 우변에 각각 대입 후 같으면 해입니다.',
  },
  {
    id: 'Q02', number: 2, category: '등식의 성질', difficulty: 'easy',
    question: '등식 x+7=15에서 x를 구하면?',
    choices: ['x=8', 'x=22', 'x=7', 'x=105'],
    answer: 0,
    explanation: [
      '양변에서 7을 빼면',
      'x+7-7 = 15-7',
      'x = 8 ✓',
    ],
    concept: '등식의 성질', misconceptionCode: 'BE01', learnStepId: 'AB02',
    misconception: '등식의 성질: 양변에서 같은 수를 빼면 등호 유지',
  },
  {
    id: 'Q03', number: 3, category: '이항', difficulty: 'easy',
    question: '2x-4=10에서 이항하여 x를 구하면?',
    choices: ['x=7', 'x=3', 'x=10', 'x=5'],
    answer: 0,
    explanation: [
      '-4를 우변으로 이항 → +4',
      '2x = 10+4 = 14',
      'x = 14÷2 = 7 ✓',
    ],
    concept: '이항', misconceptionCode: 'BE02', learnStepId: 'AB03',
    misconception: '이항 시 부호 반전: -4 이항 → +4',
  },
  {
    id: 'Q04', number: 4, category: '계수 처리', difficulty: 'easy',
    question: '5x=35에서 x는?',
    choices: ['x=7', 'x=175', 'x=30', 'x=6'],
    answer: 0,
    explanation: [
      '양변을 5로 나누면',
      '5x÷5 = 35÷5',
      'x = 7 ✓',
    ],
    concept: '계수 처리', misconceptionCode: 'BE03', learnStepId: 'AB03',
    misconception: '계수 없애기: 양변을 계수로 나누기. 5x=35 → x=7',
  },
  {
    id: 'Q05', number: 5, category: '음수 계수', difficulty: 'easy',
    question: '-3x=12에서 x는?',
    choices: ['x=-4', 'x=4', 'x=-36', 'x=36'],
    answer: 0,
    explanation: [
      '양변을 -3으로 나누면',
      '-3x÷(-3) = 12÷(-3)',
      'x = -4 ✓',
    ],
    concept: '음수 계수', misconceptionCode: 'BE03', learnStepId: 'AB03',
    misconception: '음수 계수로 나누기: 부호 주의! 12÷(-3)=-4',
  },
  {
    id: 'Q06', number: 6, category: '이항 + 계수', difficulty: 'easy',
    question: '4x+3=19에서 x는?',
    choices: ['x=4', 'x=5', 'x=16', 'x=3'],
    answer: 0,
    explanation: [
      '3을 이항: 4x = 19-3 = 16',
      '양변을 4로 나눔: x = 16÷4 = 4 ✓',
    ],
    concept: '이항 + 계수 처리', misconceptionCode: 'BE02', learnStepId: 'AB03',
    misconception: '이항 후 계수로 나누는 2단계: ①이항 ②÷계수',
  },
  {
    id: 'Q07', number: 7, category: '괄호 방정식', difficulty: 'medium',
    question: '3(x-2)=9에서 x는?',
    choices: ['x=5', 'x=3', 'x=7', 'x=1'],
    answer: 0,
    explanation: [
      '분배법칙: 3x-6=9',
      '3x = 9+6 = 15',
      'x = 15÷3 = 5 ✓',
    ],
    concept: '괄호 방정식', misconceptionCode: 'BE04', learnStepId: 'AB03',
    misconception: '괄호 방정식: 분배법칙 먼저! 3(x-2)=3x-6',
  },
  {
    id: 'Q08', number: 8, category: '괄호 방정식', difficulty: 'medium',
    question: '2(x+4)=3x-1에서 x는?',
    choices: ['x=9', 'x=7', 'x=3', 'x=5'],
    answer: 0,
    explanation: [
      '분배법칙: 2x+8=3x-1',
      '이항: 2x-3x = -1-8',
      '-x = -9 → x = 9 ✓',
    ],
    concept: '괄호 있는 복잡한 방정식', misconceptionCode: 'BE04', learnStepId: 'AB03',
    misconception: '양변에 변수 있을 때: 한쪽으로 모은 후 계수로 나누기',
  },
  {
    id: 'Q09', number: 9, category: '활용 문제', difficulty: 'medium',
    question: '어떤 수의 2배에서 7을 빼면 15가 된다. 방정식으로 나타내면?',
    choices: ['2x-7=15', '2x+7=15', '2(x-7)=15', 'x-7=15'],
    answer: 0,
    explanation: [
      '"어떤 수" = x',
      '"2배에서 7을 빼면" → 2x-7',
      '"15가 된다" → =15',
      '방정식: 2x-7=15 ✓',
    ],
    concept: '활용 문제 세우기', misconceptionCode: 'BE05', learnStepId: 'AB04',
    misconception: '"~에서 빼다" = -. "2배에서 7을 빼다" = 2x-7',
  },
  {
    id: 'Q10', number: 10, category: '활용 문제', difficulty: 'medium',
    question: '연속하는 두 정수의 합이 27이다. 작은 수를 x라 할 때 방정식은?',
    choices: ['x+(x+1)=27', 'x+(x-1)=27', '2x=27', 'x+x=27'],
    answer: 0,
    explanation: [
      '연속 두 정수: x, x+1',
      '합: x+(x+1) = 27',
      '2x+1 = 27 → 2x = 26 → x = 13',
      '두 수: 13, 14 ✓',
    ],
    concept: '연속 정수 활용', misconceptionCode: 'BE05', learnStepId: 'AB04',
    misconception: '연속하는 두 정수: x와 x+1 (연속이므로 1 차이)',
  },
  {
    id: 'Q11', number: 11, category: '거리·속력·시간', difficulty: 'medium',
    question: '시속 60 km로 달리는 자동차가 x시간 동안 240 km를 달렸다. x는?',
    choices: ['x=4', 'x=3', 'x=5', 'x=6'],
    answer: 0,
    explanation: [
      '거리 = 속력 × 시간',
      '60×x = 240',
      'x = 240÷60 = 4 ✓',
    ],
    concept: '거리·속력·시간', misconceptionCode: 'BE06', learnStepId: 'AB04',
    misconception: '거리=속력×시간. 60x=240 → x=4',
  },
  {
    id: 'Q12', number: 12, category: '나이 활용', difficulty: 'medium',
    question: '현재 아버지 나이는 아들 나이의 4배이다. 12년 후 아버지 나이가 아들 나이의 2배가 된다면, 현재 아들 나이 x를 구하는 방정식은?',
    choices: ['4x+12=2(x+12)', '4x+12=2x+12', '4x=2(x+12)', '4(x+12)=2x'],
    answer: 0,
    explanation: [
      '현재: 아들 x, 아버지 4x',
      '12년 후: 아들 x+12, 아버지 4x+12',
      '"아버지=아들의 2배": 4x+12 = 2(x+12) ✓',
    ],
    concept: '나이 활용', misconceptionCode: 'BE05', learnStepId: 'AB04',
    misconception: '미래 나이: 현재 나이 + 지난 시간. 4x+12=2(x+12)',
  },
  {
    id: 'Q13', number: 13, category: '농도·비율', difficulty: 'hard',
    question: '8% 소금물 200g에 물 xg을 더했더니 5% 소금물이 됐다. x는?',
    choices: ['x=120', 'x=80', 'x=100', 'x=60'],
    answer: 0,
    explanation: [
      '소금의 양: 200×0.08 = 16g (변하지 않음)',
      '전체 소금물: 200+x g',
      '농도: 16÷(200+x) = 0.05',
      '16 = 0.05(200+x) → 16 = 10+0.05x',
      '0.05x = 6 → x = 120 ✓',
    ],
    concept: '농도 활용', misconceptionCode: 'BE06', learnStepId: 'AB05',
    misconception: '소금량은 변하지 않음! 농도×전체=소금량으로 방정식 설정',
  },
  {
    id: 'Q14', number: 14, category: '종합 풀이', difficulty: 'hard',
    question: '5(x-3)-2(x+1)=4를 풀면?',
    choices: ['x=7', 'x=5', 'x=9', 'x=3'],
    answer: 0,
    explanation: [
      '5(x-3) = 5x-15',
      '-2(x+1) = -2x-2',
      '5x-15-2x-2 = 4',
      '3x-17 = 4',
      '3x = 21 → x = 7 ✓',
    ],
    concept: '복잡한 방정식 풀이', misconceptionCode: 'BE04', learnStepId: 'AB03',
    misconception: '각 괄호 분배 후 동류항 정리: 5x-2x=3x, -15-2=-17',
  },
  {
    id: 'Q15', number: 15, category: '검산', difficulty: 'hard',
    question: '방정식 ax+6=2x+b의 해가 x=3이고 a-b=1일 때, a+b의 값은?',
    choices: ['a+b=5', 'a+b=3', 'a+b=7', 'a+b=1'],
    answer: 0,
    explanation: [
      'x=3 대입: 3a+6 = 6+b → 3a-b = 0 ···①',
      'a-b = 1 ···②',
      '①-②: 2a = -1? → 재검토',
      '①: 3a-b=0, ②: a-b=1',
      '①-②: 2a=-1 → a=3, b=2',
      'a+b = 3+2 = 5 ✓',
    ],
    concept: '미지계수 방정식', misconceptionCode: 'BE07', learnStepId: 'AB03',
    misconception: '해를 대입해 조건 설정 후 연립으로 계수 결정',
  },
]

interface Props { onGoLearn?: (stepId: string) => void }

export default function A1_2TestPage({ onGoLearn }: Props) {
  const [phase, setPhase] = useState<'intro' | 'test' | 'result'>('intro')
  const [current, setCurrent] = useState(0)
  const [answers, setAnswers] = useState<(number | null)[]>(Array(TEST_QUESTIONS.length).fill(null))
  const [showResult, setShowResult] = useState(false)
  const [seconds, setSeconds] = useState(0)
  const [expandedExpl, setExpandedExpl] = useState<Set<number>>(new Set())
  const [videoModal, setVideoModal] = useState<{ stepId: string; label: string } | null>(null)
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const { recordTestResult, recordMisconceptions } = useProgressStore(s => ({
    recordTestResult: s.recordTestResult,
    recordMisconceptions: s.recordMisconceptions,
  }))
  const unitProgress = useUnitProgress(UNIT_ID)
  const predictedFragments = computePredictedFragments(unitProgress, 3)
  const getApprovedByTag = useVideoStore(s => s.getApprovedByTag)

  useEffect(() => {
    if (phase === 'test') {
      intervalRef.current = setInterval(() => setSeconds(s => s + 1), 1000)
    } else {
      if (intervalRef.current) clearInterval(intervalRef.current)
    }
    return () => { if (intervalRef.current) clearInterval(intervalRef.current) }
  }, [phase])

  const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, '0')}:${String(s % 60).padStart(2, '0')}`

  const handleSubmit = () => {
    if (intervalRef.current) clearInterval(intervalRef.current)
    const correct = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
    const sc = Math.round((correct / TEST_QUESTIONS.length) * 100)
    const wrongCodes = TEST_QUESTIONS
      .filter((q, i) => answers[i] !== q.answer && q.misconceptionCode)
      .map(q => q.misconceptionCode!)
    if (wrongCodes.length > 0) recordMisconceptions(UNIT_ID, wrongCodes)
    recordTestResult(UNIT_ID, {
      unitId: UNIT_ID,
      score: sc,
      correctCount: correct,
      totalCount: TEST_QUESTIONS.length,
      durationSec: seconds,
      answers: answers as number[],
      wrongConceptIds: TEST_QUESTIONS.filter((q, i) => answers[i] !== q.answer).map(q => q.concept),
      grade: sc >= 90 ? 'A' : sc >= 80 ? 'B' : sc >= 70 ? 'C' : sc >= 60 ? 'D' : 'F',
      passed: sc >= PASS_SCORE,
    })
    setPhase('result')
  }

  const correctCount = answers.filter((a, i) => a === TEST_QUESTIONS[i].answer).length
  const score = Math.round((correctCount / TEST_QUESTIONS.length) * 100)
  const passed = score >= PASS_SCORE
  const wrongQuestions = TEST_QUESTIONS.filter((_, i) => answers[i] !== TEST_QUESTIONS[i].answer)
  const answeredCount = answers.filter(a => a !== null).length

  // ── 인트로 ──────────────────────────────────────────────────────────────────
  if (phase === 'intro') {
    return (
      <div className="p-6 max-w-2xl mx-auto">
        <div className="card text-center">
          <div className="text-6xl mb-4">⚖️</div>
          <h1 className="text-2xl font-black text-slate-800 mb-1">일차방정식 실전 TEST</h1>
          <p className="text-slate-500 mb-6 text-sm">A1-2 단원 전 범위 · {TEST_QUESTIONS.length}문항 · 25분</p>
          <div className="grid grid-cols-3 gap-3 mb-6">
            {[
              { icon: <PenTool size={18} className="text-amber-500" />, label: '문항 수', value: `${TEST_QUESTIONS.length}문항` },
              { icon: <Clock size={18} className="text-orange-500" />, label: '권장 시간', value: '25분' },
              { icon: <Trophy size={18} className="text-yellow-500" />, label: '합격 기준', value: `${PASS_SCORE}점` },
            ].map(item => (
              <div key={item.label} className="bg-slate-50 rounded-xl p-3">
                <div className="flex justify-center mb-1">{item.icon}</div>
                <p className="text-xs text-slate-500">{item.label}</p>
                <p className="text-sm font-black text-slate-800">{item.value}</p>
              </div>
            ))}
          </div>
          <div className="text-left bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6">
            <p className="text-xs font-bold text-amber-700 mb-2">📋 출제 범위</p>
            <div className="grid grid-cols-2 gap-1 text-xs text-slate-600">
              {[
                'AB01 방정식과 해의 개념',
                'AB02 등식의 성질',
                'AB03 이항과 일차방정식 풀이',
                'AB04 활용 (거리·속력·시간)',
                'AB05 활용 (농도·비율)',
              ].map(t => (
                <div key={t} className="flex items-center gap-1">
                  <span className="text-amber-400">•</span> {t}
                </div>
              ))}
            </div>
          </div>
          {unitProgress.testRecords.length > 0 && (
            <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl px-4 py-3 text-left">
              <p className="text-xs font-bold text-amber-700 mb-1">📊 이전 응시 기록</p>
              <p className="text-sm font-black text-amber-800">
                최고 점수: {unitProgress.bestScore}점 {unitProgress.passed && '🏆 합격'}
              </p>
              <p className="text-xs text-amber-600">총 {unitProgress.testRecords.length}회 응시</p>
            </div>
          )}
          <button
            onClick={() => { setPhase('test'); setCurrent(0); setSeconds(0); setAnswers(Array(TEST_QUESTIONS.length).fill(null)) }}
            className="w-full py-4 rounded-2xl bg-gradient-to-r from-amber-500 to-orange-600 text-white font-black text-lg shadow-lg hover:shadow-xl transition-all"
          >
            시험 시작 →
          </button>
        </div>
      </div>
    )
  }

  // ── 시험 ────────────────────────────────────────────────────────────────────
  if (phase === 'test') {
    const q = TEST_QUESTIONS[current]
    const answered = answers[current]

    return (
      <div className="p-4 max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            <span className="text-xs font-bold text-slate-500">{current + 1} / {TEST_QUESTIONS.length}</span>
            <span className={`text-xs px-2 py-0.5 rounded-full font-bold ${
              q.difficulty === 'easy' ? 'bg-green-100 text-green-700' :
              q.difficulty === 'medium' ? 'bg-yellow-100 text-yellow-700' :
              'bg-red-100 text-red-700'
            }`}>
              {q.difficulty === 'easy' ? '쉬움' : q.difficulty === 'medium' ? '보통' : '어려움'}
            </span>
          </div>
          <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl font-mono font-bold text-sm bg-slate-100 text-slate-700">
            <Clock size={14} /> {fmt(seconds)}
          </div>
        </div>
        <div className="bg-slate-100 rounded-full h-1.5 mb-5 overflow-hidden">
          <div className="h-full bg-amber-500 rounded-full transition-all duration-300"
            style={{ width: `${((current + 1) / TEST_QUESTIONS.length) * 100}%` }} />
        </div>
        <div className="card mb-4">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-xs font-bold text-slate-400">{q.category}</span>
          </div>
          <p className="text-base font-bold text-slate-800 leading-relaxed mb-5 whitespace-pre-line">
            {q.number}. {q.question}
          </p>
          <div className="space-y-2.5">
            {q.choices.map((choice, i) => (
              <button key={i}
                onClick={() => { const next = [...answers]; next[current] = i; setAnswers(next) }}
                className={`w-full text-left px-4 py-3 rounded-xl border-2 font-medium text-sm transition-all ${
                  answered === i
                    ? 'border-amber-500 bg-amber-50 text-amber-800 shadow-sm'
                    : 'border-slate-200 bg-white hover:border-amber-300 text-slate-700'
                }`}
              >
                <span className="font-black mr-2 text-slate-400">{['①','②','③','④'][i]}</span>{choice}
              </button>
            ))}
          </div>
        </div>
        <div className="flex gap-2">
          <button disabled={current === 0} onClick={() => setCurrent(c => c - 1)}
            className="flex-1 py-2.5 rounded-xl border-2 border-slate-200 text-slate-600 font-semibold text-sm disabled:opacity-40 hover:bg-slate-50">
            ← 이전
          </button>
          {current < TEST_QUESTIONS.length - 1 ? (
            <button onClick={() => setCurrent(c => c + 1)}
              className="flex-1 py-2.5 rounded-xl bg-amber-500 text-white font-bold text-sm hover:bg-amber-600">
              다음 →
            </button>
          ) : (
            <button onClick={handleSubmit}
              className="flex-1 py-2.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-600 text-white font-black text-sm hover:opacity-90">
              제출하기 ✓
            </button>
          )}
        </div>
        <div className="mt-4 flex flex-wrap gap-1.5 justify-center">
          {TEST_QUESTIONS.map((_, i) => (
            <button key={i} onClick={() => setCurrent(i)}
              className={`w-8 h-8 rounded-lg text-xs font-bold transition-all ${
                i === current ? 'bg-amber-500 text-white' :
                answers[i] !== null ? 'bg-emerald-100 text-emerald-700' :
                'bg-slate-100 text-slate-500 hover:bg-slate-200'
              }`}>
              {i + 1}
            </button>
          ))}
        </div>
        <p className="text-center text-xs text-slate-400 mt-2">
          답변 완료: {answeredCount}/{TEST_QUESTIONS.length}
        </p>
      </div>
    )
  }

  // ── 결과 ────────────────────────────────────────────────────────────────────
  return (
    <div className="p-4 max-w-3xl mx-auto space-y-5">
      <div className={`card text-center border-2 ${passed ? 'border-emerald-300 bg-emerald-50' : 'border-orange-300 bg-orange-50'}`}>
        <div className="text-5xl mb-3">{passed ? '🏆' : '📚'}</div>
        <h2 className="text-3xl font-black mb-1" style={{ color: passed ? '#059669' : '#ea580c' }}>{score}점</h2>
        <p className={`text-base font-bold mb-4 ${passed ? 'text-emerald-700' : 'text-orange-700'}`}>
          {passed ? '🎉 합격! 일차방정식 완전 마스터!' : `아직 ${PASS_SCORE - score}점 부족해요. 다시 도전!`}
        </p>
        <div className="grid grid-cols-3 gap-3 max-w-xs mx-auto mb-5">
          {[
            { label: '정답', value: `${correctCount}문항`, color: 'text-emerald-600' },
            { label: '오답', value: `${TEST_QUESTIONS.length - correctCount}문항`, color: 'text-red-500' },
            { label: '소요 시간', value: fmt(seconds), color: 'text-slate-600' },
          ].map(item => (
            <div key={item.label} className="bg-white rounded-xl p-2">
              <p className={`text-lg font-black ${item.color}`}>{item.value}</p>
              <p className="text-xs text-slate-500">{item.label}</p>
            </div>
          ))}
        </div>
        <button
          onClick={() => {
            setPhase('intro')
            setAnswers(Array(TEST_QUESTIONS.length).fill(null))
            setSeconds(0)
            setShowResult(false)
            setExpandedExpl(new Set())
          }}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border-2 border-slate-200 text-slate-700 font-bold text-sm hover:bg-slate-50"
        >
          <RotateCcw size={14} /> 다시 풀기
        </button>
      </div>

      {/* AI 취약점 분석 */}
      {predictedFragments.length > 0 && (
        <div className="card border-2 border-amber-200 bg-amber-50">
          <div className="flex items-center gap-2 mb-3">
            <Brain size={16} className="text-amber-600" />
            <span className="text-sm font-black text-amber-800">🤖 AI 취약점 분석</span>
          </div>
          <div className="space-y-2">
            {predictedFragments.map(pred => {
              const tagConf = FRAGMENT_TAG_CONFIG[pred.tag]
              return (
                <div key={pred.fragment.id} className={`rounded-xl border ${tagConf.color} p-3`}>
                  <div className="flex items-start gap-2">
                    <span className="text-lg">{pred.fragment.emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-1.5 flex-wrap mb-0.5">
                        <span className={`text-xs px-1.5 py-0.5 rounded-full font-bold ${tagConf.badge}`}>{tagConf.label}</span>
                        <span className="text-xs font-mono text-slate-500">{pred.fragment.misconceptionCode}</span>
                      </div>
                      <p className="text-xs font-bold text-slate-800">{pred.fragment.title}</p>
                      <p className="text-xs text-slate-600 mt-0.5">{pred.fragment.fixTip}</p>
                      {onGoLearn && (
                        <button onClick={() => onGoLearn(pred.fragment.stepId)}
                          className="mt-2 text-xs px-2.5 py-1 rounded-lg bg-amber-600 text-white font-bold hover:bg-amber-700">
                          → {pred.fragment.stepTitle} 학습
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* 오답 노트 */}
      {wrongQuestions.length > 0 && (
        <div className="card">
          <button onClick={() => setShowResult(r => !r)} className="w-full flex items-center justify-between">
            <div className="flex items-center gap-2">
              <AlertTriangle size={16} className="text-orange-500" />
              <span className="text-sm font-black text-slate-800">오답 노트 ({wrongQuestions.length}문제)</span>
            </div>
            {showResult ? <ChevronUp size={16} className="text-slate-400" /> : <ChevronDown size={16} className="text-slate-400" />}
          </button>
          {showResult && (
            <div className="mt-4 space-y-4">
              {wrongQuestions.map(q => {
                const qIdx = TEST_QUESTIONS.indexOf(q)
                const myAns = answers[qIdx]
                const isExpanded = expandedExpl.has(qIdx)
                const video = getApprovedByTag(q.learnStepId ?? '')
                return (
                  <div key={q.id} className="border-2 border-red-100 rounded-xl overflow-hidden">
                    <div className="bg-red-50 px-4 py-3">
                      <div className="flex items-center gap-2 mb-1 flex-wrap">
                        <span className="text-xs font-black text-red-600">문제 {q.number}</span>
                        <span className="text-xs bg-red-100 text-red-700 px-1.5 py-0.5 rounded-full">{q.category}</span>
                        {q.misconceptionCode && (
                          <span className="text-xs font-mono bg-orange-100 text-orange-700 px-1.5 py-0.5 rounded-full">
                            {q.misconceptionCode}
                          </span>
                        )}
                      </div>
                      <p className="text-sm font-semibold text-slate-800 whitespace-pre-line">{q.question}</p>
                    </div>
                    <div className="px-4 py-3 space-y-2">
                      <div className="grid grid-cols-2 gap-1.5">
                        {q.choices.map((c, i) => (
                          <div key={i} className={`text-xs px-2.5 py-1.5 rounded-lg flex items-center gap-1 ${
                            i === q.answer
                              ? 'bg-emerald-100 text-emerald-800 font-bold border border-emerald-300'
                              : i === myAns
                                ? 'bg-red-100 text-red-700 border border-red-200'
                                : 'bg-slate-50 text-slate-500'
                          }`}>
                            {i === q.answer && <CheckCircle size={10} className="text-emerald-600 flex-shrink-0" />}
                            {i === myAns && i !== q.answer && <XCircle size={10} className="text-red-500 flex-shrink-0" />}
                            {['①','②','③','④'][i]} {c}
                          </div>
                        ))}
                      </div>
                      {q.misconception && (
                        <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                          <p className="text-xs font-bold text-amber-700">⚠️ 오개념 경고</p>
                          <p className="text-xs text-amber-800 mt-0.5">{q.misconception}</p>
                        </div>
                      )}
                      <button
                        onClick={() => setExpandedExpl(s => {
                          const n = new Set(s); n.has(qIdx) ? n.delete(qIdx) : n.add(qIdx); return n
                        })}
                        className="text-xs text-amber-600 hover:text-amber-800 flex items-center gap-1 font-semibold"
                      >
                        <Lightbulb size={11} />{isExpanded ? '해설 닫기' : '해설 보기'}
                      </button>
                      {isExpanded && (
                        <div className="bg-slate-50 rounded-xl p-3 space-y-1">
                          {q.explanation.map((line, i) => (
                            <p key={i} className="text-xs text-slate-700 leading-relaxed">{line}</p>
                          ))}
                        </div>
                      )}
                      <div className="flex gap-2 flex-wrap">
                        {onGoLearn && q.learnStepId && (
                          <button onClick={() => onGoLearn(q.learnStepId!)}
                            className="text-xs px-3 py-1.5 rounded-lg bg-amber-500 text-white font-bold hover:bg-amber-600 flex items-center gap-1">
                            <BookOpen size={11} /> 개념 다시 보기 ({q.learnStepId})
                          </button>
                        )}
                        {video && (
                          <button onClick={() => setVideoModal({ stepId: q.learnStepId!, label: q.concept })}
                            className="text-xs px-3 py-1.5 bg-red-100 text-red-700 rounded-lg font-semibold hover:bg-red-200 flex items-center gap-1">
                            <Youtube size={11} /> 영상
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      )}

      {/* 전체 답안 */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <BarChart2 size={16} className="text-slate-500" />
          <span className="text-sm font-bold text-slate-700">전체 답안 확인</span>
        </div>
        <div className="grid grid-cols-5 gap-1.5">
          {TEST_QUESTIONS.map((q, i) => {
            const correct = answers[i] === q.answer
            return (
              <div key={i} className={`rounded-lg p-2 text-center ${
                correct ? 'bg-emerald-50 border border-emerald-200' : 'bg-red-50 border border-red-200'
              }`}>
                <p className="text-xs font-bold text-slate-500">{i + 1}번</p>
                {correct
                  ? <CheckCircle size={14} className="text-emerald-500 mx-auto" />
                  : <XCircle size={14} className="text-red-500 mx-auto" />}
              </div>
            )
          })}
        </div>
      </div>

      {videoModal && (
        <ConceptVideoModal
          conceptTag={videoModal.stepId}
          conceptLabel={videoModal.label}
          viewerId="student_001"
          onClose={() => setVideoModal(null)}
          onCompleted={() => setVideoModal(null)}
        />
      )}
    </div>
  )
}
